import classNames from 'classnames';
import { AlertCircle, AlertTriangle, Check, X } from 'react-feather';
import { useTranslation } from 'react-i18next';

import { useMutation } from '../../Shared/api';
import { useNotifier } from '../../Shared/Notifier/NotificationProvider';
import { MollieConnection } from '../../Shared/types';
import { AButton, Button } from '../../Shared/UI/Button';
import { Card } from '../../Shared/UI/Card';
import { Icon } from '../../Shared/UI/Icon';
import { useRouter } from '../router';

export default function MollieConnectionCard({
  connection,
}: {
  connection: MollieConnection;
}) {
  const { t } = useTranslation();
  const { routes } = useRouter();
  const notifier = useNotifier();

  const [deleteConnection, { loading: deleting }] = useMutation(routes.delete_mollie_connection(connection.id), {
    onSuccess: () => {
      notifier.notify(t('backend:mollie_connection_deleted'));
    },
    onError: () => {
      notifier.notify(t('backend:something_went_wrong'), AlertTriangle);
    },
  });

  return (
    <Card>
      <h3 className={classNames('mb-2')}>
        {connection.title || connection.id}
      </h3>
      {connection.connected && connection.can_receive_payments && connection.profiles.length > 0 && (
        <>
          <div className="flex text-slate-700">
            <Icon className="!mt-1 mr-2">
              <Check />
            </Icon>
            {t('backend:mollie_account_connected')}
          </div>
          <AButton
            href={connection.dashboard_url}
            target="_blank" rel="noopener noreferrer"
            variant="secondary"
            className="mt-4 mr-2"
          >
            {t('backend:open_mollie_dashboard')}
          </AButton>
        </>
      )}
      {connection.connected && connection.can_receive_payments && connection.profiles.length === 0 && (
        <>
          <div className="flex text-slate-600">
            <Icon className="!mt-1 mr-2">
              <AlertCircle />
            </Icon>
            {t('backend:mollie_require_website_profile')}
          </div>
          <AButton
            href={`${connection.dashboard_url}/settings/profiles`}
            target="_blank" rel="noopener noreferrer"
            className="mt-4 mr-2"
          >
            {t('backend:create_website_profile')}
          </AButton>
        </>
      )}
      {connection.connected && !connection.can_receive_payments && !connection.onboarding_url && (
        <>
          <div className="flex text-slate-600">
            <Icon className="!mt-1 mr-2">
              <AlertCircle />
            </Icon>
            {t('backend:mollie_reviewing')}
          </div>
          <AButton
            href={connection.dashboard_url}
            target="_blank" rel="noopener noreferrer"
            className="mt-4 mr-2"
          >
            {t('backend:view_status')}
          </AButton>
        </>
      )}
      {connection.connected && connection.onboarding_url && (
        <>
          <div className="flex text-slate-600">
            <Icon className="!mt-1 mr-2">
              <AlertCircle />
            </Icon>
            {t('backend:mollie_needs_information')}
          </div>
          <AButton
            href={connection.onboarding_url}
            target="_blank" rel="noopener noreferrer"
            className="mt-4 mr-2"
          >
            {t('backend:enter_information')}
          </AButton>
        </>
      )}
      {!connection.connected && (
        <>
          <div className="flex text-slate-600">
            <Icon className="!mt-1 mr-2">
              <X />
            </Icon>
            {t('backend:mollie_account_disconnected')}
          </div>
          <AButton
            href={connection.dashboard_url}
            target="_blank" rel="noopener noreferrer"
            variant="secondary"
            className="mt-4 mr-2"
          >
            {t('backend:open_mollie_dashboard')}
          </AButton>
        </>
      )}
      {connection.deletable && (
        <Button
          onClick={() => deleteConnection()}
          loading={deleting}
          variant="danger"
          className="mt-4"
        >
          {t('backend:delete')}
        </Button>
      )}
    </Card>
  );
}
